import React, { useMemo, useState } from "react";
import { Box, ButtonBase, Chip, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ReviewHorizontalCarousel from "./ReviewHorizontalCarousel";
import ActionModal from "../shared/ActionModal";
import NoticeboardButton from "../shared/NoticeboardButton";
import SearchField from "../shared/SearchField";
import { BackgroundOption, getSystemAssetThumbnail } from "../../config/assets";
import { BACKGROUND_CATEGORIES, backgroundCategory, filterBackgrounds, suggestedBackgrounds } from "../../utils/backgroundCatalogue";

type Props = {
  options: BackgroundOption[];
  value: string;
  onChange: (value: string) => void;
  noticeText?: string;
};

export default function NoticeBackgroundPicker({ options, value, onChange, noticeText = "" }: Props) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const categories = BACKGROUND_CATEGORIES.filter(group => options.some(option => option.value && backgroundCategory(option) === group));
  const suggestions = useMemo(() => suggestedBackgrounds(options, noticeText), [options, noticeText]);
  const filtered = useMemo(() => filterBackgrounds(options, category, search), [options, category, search]);
  const label = (option: BackgroundOption) => option.value ? option.label : "No background";
  const browse = (group = "All") => { setCategory(group); setSearch(""); setOpen(true); };
  const pick = (next: string) => { onChange(next); setOpen(false); };

  const thumbnail = (option: BackgroundOption, caption = true) => (
    <Box key={option.value || "default"} sx={{ minWidth: 0 }}>
      <ButtonBase
        aria-label={`Use ${label(option)} background`}
        aria-pressed={value === option.value}
        onClick={() => pick(option.value)}
        sx={{ width: "100%", aspectRatio: "1", borderRadius: 2, overflow: "hidden", position: "relative", border: "2px solid", borderColor: value === option.value ? "primary.main" : "divider", bgcolor: alpha(theme.palette.primary.main, 0.1) }}
      >
        {option.value ? <Box component="img" src={getSystemAssetThumbnail(option.value)} alt="" loading="lazy" sx={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <PaletteOutlinedIcon color="primary" />}
        {value === option.value && <Box sx={{ position: "absolute", right: 4, top: 4, bgcolor: "primary.main", color: "common.white", borderRadius: "50%", display: "flex", p: 0.4 }}><CheckIcon sx={{ fontSize: 16 }} /></Box>}
      </ButtonBase>
      {caption && <Typography variant="caption" component="div" sx={{ textAlign: "center", mt: 0.5, overflowWrap: "anywhere" }}>{label(option)}</Typography>}
    </Box>
  );

  return <>
    <Box sx={{ p: { xs: 1.5, sm: 2 }, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) auto", columnGap: 1, alignItems: "center", mb: 1.5 }}>
        <PaletteOutlinedIcon color="primary" sx={{ gridRow: "1 / 3" }} />
        <Typography fontWeight={600}>Background</Typography>
        <NoticeboardButton variant="secondary" size="small" endIcon={<ChevronRightIcon />} onClick={() => browse()} aria-label="Browse more backgrounds">Browse more</NoticeboardButton>
        <Typography variant="caption" color="text.secondary" sx={{ gridColumn: "2 / 4" }}>Choose a background for your notice</Typography>
      </Box>
      <ReviewHorizontalCarousel contentKey={options.map(option => option.value || "default").join("|")}>
        <Box sx={{ display: "flex", gap: 1.15, width: "max-content", py: 0.5 }}>
          {options.map(option => <Box key={option.value || "default"} sx={{ width: 74, flexShrink: 0 }}>{thumbnail(option, false)}</Box>)}
        </Box>
      </ReviewHorizontalCarousel>
    </Box>
    <ActionModal open={open} onClose={() => setOpen(false)} title="Choose a background" headerStyle="minimal" maxWidth="md">
      <SearchField value={search} onChange={setSearch} placeholder="Search backgrounds..." />
      <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1} sx={{ my: 2 }}>
        {["All", ...categories].map(group => <Chip key={group} label={group} clickable color={category === group ? "primary" : "default"} aria-pressed={category === group} onClick={() => setCategory(group)} />)}
      </Stack>
      {category === "All" && !search.trim() && <>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Suggested for this notice</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(3, minmax(0, 1fr))", sm: "repeat(5, minmax(0, 1fr))" }, gap: 1, mb: 3 }}>{suggestions.map(option => thumbnail(option))}</Box>
      </>}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>{category === "All" ? "All backgrounds" : `${category} backgrounds`}</Typography>
      <Typography variant="caption" color="text.secondary" component="div" aria-live="polite" sx={{ mb: 1 }}>{filtered.length} backgrounds</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(3, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))", md: "repeat(5, minmax(0, 1fr))" }, gap: 1.5 }}>{filtered.map(option => thumbnail(option))}</Box>
      {!filtered.length && <Box sx={{ py: 4, textAlign: "center" }}><Typography color="text.secondary">No backgrounds match your search.</Typography><NoticeboardButton variant="tertiary" onClick={() => { setSearch(""); setCategory("All"); }}>Clear filters</NoticeboardButton></Box>}
    </ActionModal>
  </>;
}
